import React, { lazy, Suspense } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import ErrorBoundary from "@/components/ErrorBoundary";

// Auth — single source of truth now lives in contexts/AuthContext
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
// Re-export so existing pages can keep importing from '@/App'
export { useAuth } from '@/contexts/AuthContext';
export { API } from '@/services/api';

// Dealer Portal Pages
import LoginPage from "@/pages/LoginPage";
import ForcePasswordChangePage from "@/pages/ForcePasswordChangePage";
import RegisterPage from "@/pages/RegisterPage";
import PublicLossCalculatorPage from "@/pages/PublicLossCalculatorPage";
import AuditThankYouPage from "@/pages/AuditThankYouPage";
import TVDisplay from "@/pages/dealer/TVDisplay";
import SalespersonLayout from "@/components/layout/SalespersonLayout";

// Layouts
import DashboardLayout from "@/components/layout/DashboardLayout";
import AdminLayout from "@/components/layout/AdminLayout";

// Dealer Portal Pages (lazy)
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const InboxPage = lazy(() => import("@/pages/InboxPage"));
const ContactsPage = lazy(() => import("@/pages/ContactsPage"));
const PipelinePage = lazy(() => import("@/pages/PipelinePage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const AgentsPage = lazy(() => import("@/pages/AgentsPage"));
const FollowUpsPage = lazy(() => import("@/pages/FollowUpsPage"));
const AutomationsPage = lazy(() => import("@/pages/AutomationsPage"));
const CalendarPage = lazy(() => import("@/pages/CalendarPage"));
const ReportsPage = lazy(() => import("@/pages/ReportsPage"));
const TemplatesPage = lazy(() => import("@/pages/TemplatesPage"));
const ScriptsPage = lazy(() => import("@/pages/ScriptsPage"));
const TeamPage = lazy(() => import("@/pages/TeamPage"));
const CampaignsPage = lazy(() => import("@/pages/CampaignsPage"));
const OutboundCampaignBuilder = lazy(() => import("@/pages/dealer/campaigns/OutboundCampaignBuilder"));
const CampaignTracker = lazy(() => import("@/pages/dealer/campaigns/CampaignTracker"));
const CompetitorConquestPage = lazy(() => import("@/pages/dealer/CompetitorConquestPage"));
const InventoryPage = lazy(() => import("@/pages/InventoryPage"));
const LeaderboardPage = lazy(() => import("@/pages/LeaderboardPage"));
const ApprovalsPage = lazy(() => import("@/pages/ApprovalsPage"));
const PerformancePage = lazy(() => import("@/pages/dealer/PerformancePage"));
const DNCAuditPage = lazy(() => import("@/pages/dealer/DNCAuditPage"));
const AddOnsPage = lazy(() => import("@/pages/dealer/AddOnsPage"));
const DealerBillingPage = lazy(() => import("@/pages/dealer/DealerBillingPage"));
const CustomerJourneyFunnelPage = lazy(() => import("@/pages/dealer/CustomerJourneyFunnelPage"));
const DealVelocityPage = lazy(() => import("@/pages/dealer/DealVelocityPage"));
const AIDealAnalyzerPage = lazy(() => import("@/pages/dealer/AIDealAnalyzerPage"));
const AppointmentShowPredictorPage = lazy(() => import("@/pages/dealer/AppointmentShowPredictorPage"));
const AISalesCoachPage = lazy(() => import("@/pages/dealer/AISalesCoachPage"));
const ReviewGenerationPage = lazy(() => import("@/pages/dealer/ReviewGenerationPage"));
const InventoryAgingPage = lazy(() => import("@/pages/dealer/InventoryAgingPage"));
const MultiLocationPage = lazy(() => import("@/pages/dealer/MultiLocationPage"));
const PredictiveInventoryPage = lazy(() => import("@/pages/dealer/PredictiveInventoryPage"));
const StreakTrackingPage = lazy(() => import("@/pages/dealer/StreakTrackingPage"));
const SmartNotificationsPage = lazy(() => import("@/pages/dealer/SmartNotificationsPage"));

// Salesperson pages (lazy)
const SalespersonHomePage = lazy(() => import("@/pages/salesperson/SalespersonHomePage"));
const SalespersonTodayPage = lazy(() => import("@/pages/salesperson/SalespersonTodayPage"));
const SalespersonStatsPage = lazy(() => import("@/pages/salesperson/SalespersonStatsPage"));
const SalespersonMePage = lazy(() => import("@/pages/salesperson/SalespersonMePage"));

// Admin pages (lazy)
const AdminOverviewPage = lazy(() => import("@/pages/admin/AdminOverviewPage"));
const AdminDealershipsPage = lazy(() => import("@/pages/admin/AdminDealershipsPage"));
const AdminUsersPage = lazy(() => import("@/pages/admin/AdminUsersPage"));
const AdminAnalyticsPage = lazy(() => import("@/pages/admin/AdminAnalyticsPage"));
const AdminBillingPage = lazy(() => import("@/pages/admin/AdminBillingPage"));
const AdminDemosPage = lazy(() => import("@/pages/admin/AdminDemosPage"));
const AdminSettingsPage = lazy(() => import("@/pages/admin/AdminSettingsPage"));
const AdminAISettingsPage = lazy(() => import("@/pages/admin/AdminAISettingsPage"));
const AdminAgentsPage = lazy(() => import("@/pages/admin/AdminAgentsPage"));
const AdminOnboardingPage = lazy(() => import("@/pages/admin/AdminOnboardingPage"));
const AdminAgentAnalyticsPage = lazy(() => import("@/pages/admin/AdminAgentAnalyticsPage"));
const AdminABTestingPage = lazy(() => import("@/pages/admin/AdminABTestingPage"));
const AdminFeaturesPage = lazy(() => import("@/pages/admin/AdminFeaturesPage"));
const AdminAuditLeadsPage = lazy(() => import("@/pages/admin/AdminAuditLeadsPage"));
const SecurityPage = lazy(() => import("@/pages/admin/SecurityPage"));

// Loading Spinner
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex items-center gap-2">
      <div className="w-2 h-2 bg-primary rounded-full animate-pulse-dot" />
      <div className="w-2 h-2 bg-primary rounded-full animate-pulse-dot-delay-1" />
      <div className="w-2 h-2 bg-primary rounded-full animate-pulse-dot-delay-2" />
    </div>
  </div>
);

// Protected Route - redirects based on role
const ProtectedRoute = ({ children, requireSuperAdmin = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingSpinner />;

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requireSuperAdmin && user.role !== 'super_admin') {
    return <Navigate to="/dashboard" replace />;
  }

  if (user?.must_change_password && location.pathname !== '/change-password-required') {
    return <Navigate to="/change-password-required" replace />;
  }

  return children;
};

const RoleRoute = ({ allow, redirectTo, children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  const role = user?.role || '';
  if (!allow.includes(role)) return <Navigate to={redirectTo} replace />;
  return children;
};

// Public Route (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) return <LoadingSpinner />;

  if (user) {
    if (user.role === 'super_admin') {
      return <Navigate to="/admin" replace />;
    }
    if (user.role === 'sales_rep' || user.role === 'dealer_user') return <Navigate to="/my-today" replace />;
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Root redirect based on role
const RootRedirect = () => {
  const { user, loading } = useAuth();
  
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'super_admin') return <Navigate to="/admin" replace />;
  if (user.role === 'sales_rep' || user.role === 'dealer_user') return <Navigate to="/my-today" replace />;
  return <Navigate to="/dashboard" replace />;
};

const Safe = ({ children }) => <ErrorBoundary>{children}</ErrorBoundary>;

const PageLoader = () => (
  <div className="flex items-center justify-center h-screen bg-gray-50">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      <p className="text-sm text-gray-500">
        Loading...
      </p>
    </div>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
          <Route path="/tv-display" element={<Safe><TVDisplay /></Safe>} />
          {/* Public Routes */}
          {/* Public audit / loss calculator — same component; use ?ref=APT-… for outreach tracking */}
          <Route path="/loss-calculator" element={<Safe><PublicLossCalculatorPage /></Safe>} />
          <Route path="/audit" element={<Safe><PublicLossCalculatorPage /></Safe>} />
          <Route path="/audit/thank-you" element={<Safe><AuditThankYouPage /></Safe>} />
          <Route path="/login" element={<PublicRoute><Safe><LoginPage /></Safe></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><Safe><RegisterPage /></Safe></PublicRoute>} />
          <Route path="/change-password-required" element={<ProtectedRoute><ForcePasswordChangePage /></ProtectedRoute>} />

          {/* Admin Portal Routes (super_admin only) */}
          <Route path="/admin" element={<ProtectedRoute requireSuperAdmin><Safe><AdminLayout /></Safe></ProtectedRoute>}>
            <Route index element={<AdminOverviewPage />} />
            <Route path="analytics" element={<AdminAnalyticsPage />} />
            <Route path="dealerships" element={<AdminDealershipsPage />} />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="agents" element={<AdminAgentsPage />} />
            <Route path="ai-settings" element={<AdminAISettingsPage />} />
            <Route path="approvals" element={<ApprovalsPage />} />
            <Route path="onboarding" element={<AdminOnboardingPage />} />
            <Route path="agent-analytics" element={<AdminAgentAnalyticsPage />} />
            <Route path="ab-testing" element={<AdminABTestingPage />} />
            <Route path="billing" element={<AdminBillingPage />} />
            <Route path="demos" element={<AdminDemosPage />} />
            <Route path="audit-leads" element={<AdminAuditLeadsPage />} />
            <Route path="settings" element={<AdminSettingsPage />} />
            <Route path="features" element={<AdminFeaturesPage />} />
            <Route path="security" element={<SecurityPage />} />
          </Route>

          {/* Dealer Portal Routes */}
          <Route path="/" element={<ProtectedRoute><RootRedirect /></ProtectedRoute>} />

          {/* Manager / Admin full portal */}
          <Route
            path="/"
            element={
              <RoleRoute allow={['dealer_admin', 'manager', 'admin', 'super_admin', 'viewer']} redirectTo="/my-home">
                <Safe><DashboardLayout /></Safe>
              </RoleRoute>
            }
          >
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="inbox" element={<InboxPage />} />
            <Route path="inbox/:contactId" element={<InboxPage />} />
            <Route path="contacts" element={<ContactsPage />} />
            <Route path="approvals" element={<ApprovalsPage />} />
            <Route path="pipeline" element={<PipelinePage />} />
            <Route path="agents" element={<AgentsPage />} />
            <Route path="followups" element={<FollowUpsPage />} />
            <Route path="automations" element={<AutomationsPage />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="add-ons" element={<AddOnsPage />} />
            <Route path="billing" element={<DealerBillingPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="templates" element={<TemplatesPage />} />
            <Route path="scripts" element={<ScriptsPage />} />
            <Route path="team" element={<TeamPage />} />
            <Route path="campaigns" element={<CampaignsPage />} />
            <Route path="campaigns/outbound" element={<OutboundCampaignBuilder />} />
            <Route path="campaigns/outbound/track/:campaignId" element={<CampaignTracker />} />
            <Route path="competitors" element={<CompetitorConquestPage />} />
            <Route path="inventory" element={<InventoryPage />} />
            <Route path="leaderboard" element={<LeaderboardPage />} />
            <Route path="performance" element={<PerformancePage />} />
            <Route path="dnc-audit" element={<DNCAuditPage />} />
            <Route path="funnel" element={<CustomerJourneyFunnelPage />} />
            <Route path="velocity" element={<DealVelocityPage />} />
            <Route path="deal-analyzer" element={<AIDealAnalyzerPage />} />
            <Route path="show-predictor" element={<AppointmentShowPredictorPage />} />
            <Route path="sales-coach" element={<AISalesCoachPage />} />
            <Route path="reviews" element={<ReviewGenerationPage />} />
            <Route path="inventory-aging" element={<InventoryAgingPage />} />
            <Route path="multi-location" element={<MultiLocationPage />} />
            <Route path="predictive-inventory" element={<PredictiveInventoryPage />} />
            <Route path="streaks" element={<StreakTrackingPage />} />
            <Route path="smart-notifications" element={<SmartNotificationsPage />} />
          </Route>

          {/* Salesperson simplified portal */}
          <Route
            path="/"
            element={
              <RoleRoute allow={['sales_rep', 'dealer_user']} redirectTo="/dashboard">
                <Safe><SalespersonLayout /></Safe>
              </RoleRoute>
            }
          >
            <Route path="my-today" element={<SalespersonTodayPage />} />
            <Route path="my-home" element={<SalespersonHomePage />} />
            <Route path="my-leads" element={<InboxPage />} />
            <Route path="my-leads/:contactId" element={<InboxPage />} />
            <Route path="my-stats" element={<SalespersonStatsPage />} />
            <Route path="me" element={<SalespersonMePage />} />

            {/* Hard redirects for manager-only routes */}
            <Route path="reports" element={<Navigate to="/my-stats" replace />} />
            <Route path="settings" element={<Navigate to="/my-home" replace />} />
            <Route path="team" element={<Navigate to="/my-home" replace />} />
            <Route path="admin/*" element={<Navigate to="/my-home" replace />} />
          </Route>

          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
